import type { Content } from "@prismicio/client";
import type { SliceComponentProps } from "@prismicio/react";

import { SliceBox } from "@cms/components/SliceBox";
import { useSliceContext } from "@cms/context";
import { GameTags, getGameMetadata, sanitizeTag } from "@cms/utils";
import { RenderOnceInView } from "@ui-kit";
import UIKitGamesGrid, {
  type GamesGridProps as UIKitGamesGridProps,
} from "@ui-kit/GamesGrid";
import { useMemo } from "react";

export type GameCollections = Record<string, Content.GameDocument[]>;

/**
 * Props for `GamesGrid`.
 */
export type GamesGridProps = SliceComponentProps<Content.GamesGridSlice>;

const getProps = (
  slice: Content.GamesGridSlice,
  gameCollections: GameCollections,
): UIKitGamesGridProps => {
  const tag = slice.primary?.filter_by_tag
    ? sanitizeTag(slice.primary.tag)
    : GameTags.Untagged;
  const gamesDocs = tag ? gameCollections?.[tag] : [];

  if (!gamesDocs?.length) return { games: [], tag: tag };

  return {
    tag,
    games: gamesDocs
      .map(getGameMetadata)
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
};

/**
 * Component for "GamesGrid" Slices.
 */
const GamesGrid = ({ slice, index }: GamesGridProps): JSX.Element => {
  const ctx = useSliceContext();
  const data = ctx.data as { gameCollections: GameCollections };
  const sliceChildren = useMemo(
    () => <UIKitGamesGrid {...getProps(slice, data.gameCollections)} />,
    [slice, data.gameCollections],
  );

  if (!data.gameCollections) return <></>;

  return (
    <SliceBox slice={slice} index={index} mb="base.spacing.x12">
      {/* 
        @NOTE: render the featured section as part of the initial SSR, but leave out the rest.
        This helps keep the initial page load lean, by keeping the initial dom count under 1000
       */}
      {slice.primary.tag === "featured" ? (
        sliceChildren
      ) : (
        <RenderOnceInView>{sliceChildren}</RenderOnceInView>
      )}
    </SliceBox>
  );
};

export default GamesGrid;
